class ZentusCardVideo extends HTMLElement {
  #controller;
  #observer;
  #video;
  #hovering = false;
  #generation = 0;
  #hoverMedia = matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const card = this.closest('.z-product-card, product-card');
    card?.addEventListener('pointerenter', this.#start, { signal });
    card?.addEventListener('pointerleave', this.#stop, { signal });
    card?.addEventListener('pointercancel', this.#stop, { signal });
    document.addEventListener('visibilitychange', this.#stop, { signal });
    this.#hoverMedia.addEventListener('change', this.#stop, { signal });
    this.#observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => !entry.isIntersecting)) this.#stop();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.#controller?.abort();
    this.#observer?.disconnect();
    if (this.#video) {
      this.#video.removeAttribute('src');
      this.#video.remove();
      this.#video = undefined;
    }
  }

  #start = async (event) => {
    if (event.pointerType !== 'mouse' || !this.#hoverMedia.matches || document.hidden || !this.dataset.src) return;
    this.#hovering = true;
    const generation = ++this.#generation;
    if (this.#video && this.#video.parentNode !== this) {
      this.#video.pause();
      this.#video.removeEventListener('error', this.#stop);
      this.#video.removeAttribute('src');
      this.#video = undefined;
    }
    if (!this.#video) {
      this.#video = document.createElement('video');
      this.#video.muted = true;
      this.#video.defaultMuted = true;
      this.#video.loop = true;
      this.#video.playsInline = true;
      this.#video.preload = 'none';
      this.#video.hidden = true;
      this.#video.setAttribute('aria-hidden', 'true');
      this.#video.addEventListener('error', this.#stop, { signal: this.#controller.signal });
      this.#video.src = this.dataset.src;
      this.append(this.#video);
    }
    const video = this.#video;
    try {
      await video.play();
      if (generation !== this.#generation) {
        if (!this.#hovering) video.pause();
        return;
      }
      if (!this.#hovering || !this.isConnected) { video.pause(); return; }
      video.hidden = false;
    } catch {
      if (generation === this.#generation) this.#stop();
    }
  };

  #stop = () => {
    this.#hovering = false;
    this.#generation++;
    if (!this.#video) return;
    this.#video.pause();
    this.#video.hidden = true;
    try { this.#video.currentTime = 0; } catch { /* The file may not have loaded yet. */ }
  };
}

if (!customElements.get('zentus-card-video')) customElements.define('zentus-card-video', ZentusCardVideo);
